import React from 'react';

import { Link } from 'react-router-dom';

import {
  AppstoreOutlined,
  BuildOutlined,
  CalculatorOutlined,
  CalendarOutlined,
  CaretDownFilled,
  CaretUpFilled,
  CheckCircleOutlined,
  CodeOutlined,
  DashboardOutlined,
  DownOutlined,
  EnvironmentOutlined,
  FlagOutlined,
  FundOutlined,
  InsertRowBelowOutlined,
  MonitorOutlined,
  ReconciliationOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  StockOutlined,
  StopOutlined,
  TagOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { css } from '@emotion/css';
import {
  RecurrencyReplenishmentMethod,
  P21SafetyStockType,
  P21LeadTimeSource,
  TenantFeatureFlag,
  PlanningUsageInheritanceType,
  DailyWorkflowTaskResolution,
  ExclusionSource,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { SearchPlanningDTO } from '@recurrency/core-api-schema/dist/search/getSearchPlanning';
import { TenantDTO } from '@recurrency/core-api-schema/dist/tenants/tenantDTO';
import { Menu } from 'antd';
import { theme } from 'theme';

import { ItemWithResolutionData } from 'pages/dailyWorkflow/types';
import { changeRecordFlagStatus } from 'pages/purchasing/PlanningPage/modals/FlagItemStatusUpdater';
import { changeRecordExcludedStatus } from 'pages/purchasing/PlanningPage/modals/IncludeExcludeStatusUpdater';
import { MultiLeadTimeModal } from 'pages/purchasing/PlanningPage/modals/MultiLeadTimeModal';
import { MultiSafetyStockModal } from 'pages/purchasing/PlanningPage/modals/MultiSafetyStockModal';
import { MultiStockFloorModal } from 'pages/purchasing/PlanningPage/modals/MultiStockFloorModal';
import { ProjectedInventoryChangePopover } from 'pages/purchasing/PlanningPage/modals/ProjectedInventoryChangePopover';
import { RecommendedMaxPopover } from 'pages/purchasing/PlanningPage/modals/RecommendedMaxPopover';
import { RecommendedMinPopover } from 'pages/purchasing/PlanningPage/modals/RecommendedMinPopover';
import { UpdateReplenishmentSettingsModal } from 'pages/purchasing/PlanningPage/modals/UpdateReplenishmentSettingsModal';
import {
  leadTimeSourceNames,
  MultiModalSelection,
  safetyStockSourceNames,
} from 'pages/purchasing/PlanningPage/modals/utils';
import { ReplenishmentPathPopover } from 'pages/purchasing/PlanningPage/ReplenishmentPathPopover';
import {
  demandPatternChangeOptions,
  demandPatternOptions,
  demandPredictabilityOptions,
} from 'pages/purchasing/PlanningPage/utils';
import { ReplenishmentMethod, productTypeFacetOptions } from 'pages/purchasing/utils';

import { Button } from 'components/Button';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { Dropdown } from 'components/Dropdown';
import { FlexSpace } from 'components/FlexSpace';
import { NetStockPopover, NetStockRecord } from 'components/recipes/equation/NetStockPopover';
import { LocationLabel } from 'components/recipes/LocationLabel';
import { PlanningStatusBadge } from 'components/recipes/PlanningStatusBadge';
import { ValueFacet, FacetRenderType } from 'components/recipes/SearchFrame/types';
import { BadgeStatus, labelByStatus } from 'components/recipes/StatusBadge';
import { Tooltip } from 'components/Tooltip';
import { DemandPatternTooltip } from 'components/Tooltip/DemandPatternTooltip';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';
import { EllipsisTruncatedParagraph } from 'components/Typography/EllipsisTruncatedLabel';

import { ExtendedUser } from 'hooks/useGlobalApp';

import { showAsyncModal } from 'utils/asyncModal';
import { formatDate, formatNumber, formatPercent, formatUSD, getChangeColor } from 'utils/formatting';
import { shouldShowFeatureFlag } from 'utils/roleAndTenant';
import { routes } from 'utils/routes';
import { PersistedColumn } from 'utils/tableAndSidePaneSettings/types';
import {
  asKeyOf,
  numberColumn,
  sortableChangePercentColumn,
  sortableDateColumn,
  sortableNumberColumn,
  sortDirections,
} from 'utils/tables';
import { isTenantErpTypeNetSuite, isTenantErpTypeP21 } from 'utils/tenants';
import { getTenantSetting, isHubAndSpokeEnabled } from 'utils/tenantSettings';

export const getPlanningColumns = (config: {
  activeTenant: TenantDTO;
  activeUser: ExtendedUser;
  isOpOqVariant: boolean;
  searchIndexReload: (() => void) | undefined;
  openSparseItemGrid?: () => void;
}): (PersistedColumn<SearchPlanningDTO> | null)[] => {
  const isPlanningHubAndSpoke = isHubAndSpokeEnabled();
  const isP21 = isTenantErpTypeP21(config.activeTenant.erpType);

  // Feature flag impacted columns

  const inheritanceEnabled =
    (getTenantSetting(TenantSettingKey.FeaturePlanningUsageInheritance) ?? PlanningUsageInheritanceType.None) !==
    PlanningUsageInheritanceType.None;
  const createdDateTooltipText = getCreatedDateTooltipText(inheritanceEnabled, isHubAndSpokeEnabled());
  const firstUsageDateTooltipText = getFirstSoldDateTooltipText(inheritanceEnabled, isHubAndSpokeEnabled());
  // This will give certain tenants back the old columns for min/max
  const shouldShowMinMaxColumns = shouldShowFeatureFlag(
    config.activeTenant,
    config.activeUser,
    TenantFeatureFlag.FeaturePlanningShowMinMax,
  );

  return [
    {
      title: 'Item UID',
      settingKey: 'itemUid',
      dataIndex: asKeyOf<SearchPlanningDTO>('item_uid'),
      fixed: 'left',
      sorter: true,
      sortDirections,
      optional: !isTenantErpTypeNetSuite(config.activeTenant.erpType),
    },
    {
      title: 'Item ID',
      settingKey: 'itemId',
      dataIndex: asKeyOf<SearchPlanningDTO>('item_id'),
      fixed: 'left',
      sorter: true,
      render: (_: string, record: SearchPlanningDTO) => (
        <div>
          <Link to={routes.purchasing.itemDetails(record.item_id)}>{record.item_id}</Link>
        </div>
      ),
      sortDirections,
      required: true,
    },
    {
      title: 'Item Name',
      settingKey: 'itemName',
      dataIndex: asKeyOf<SearchPlanningDTO>('item_name'),
      fixed: 'left',
      sorter: true,
      sortDirections,
      required: true,
    },
    {
      title: `${isPlanningHubAndSpoke ? 'Requirement ' : ''}Location`,
      dataIndex: asKeyOf<SearchPlanningDTO>('location'),
      settingKey: 'location',
      fixed: 'left',
      sorter: isPlanningHubAndSpoke,
      required: true,
      render: (location: string) => <LocationLabel value={location} />,
    },
    isPlanningHubAndSpoke
      ? {
          title: (
            <InfoTooltip title="The location where this item is replenished from according to its replenishment path">
              Replenishment Location
            </InfoTooltip>
          ),
          dataIndex: asKeyOf<SearchPlanningDTO>('replenishment_location'),
          settingKey: 'replenishmentLocation',
          fixed: 'left',
          sorter: true,
          render: (replenishmentLocation, record) => (
            <ConditionalWrapper
              condition={
                !!record.replenishment_location &&
                record.location !== record.replenishment_location &&
                record.replenishment_location.trim() !== ':'
              }
              wrapper={(children) => (
                <ReplenishmentPathPopover
                  itemId={record.item_uid}
                  location={record.location}
                  supplier={record.primary_supplier}
                >
                  {children}
                </ReplenishmentPathPopover>
              )}
            >
              <LocationLabel value={replenishmentLocation} />
            </ConditionalWrapper>
          ),
        }
      : null,
    getTenantSetting(TenantSettingKey.UiShowShortCode) ?? false
      ? {
          title: 'Short Code',
          settingKey: 'shortCode',
          dataIndex: asKeyOf<SearchPlanningDTO>('short_code'),
        }
      : null,
    isP21
      ? {
          title: 'ABC Class',
          settingKey: 'purchaseClass',
          dataIndex: asKeyOf<SearchPlanningDTO>('purchase_class'),
          sorter: true,
          sortDirections,
        }
      : null,
    getProjectedInventoryChangeColumn(config.isOpOqVariant),
    !config.isOpOqVariant && !shouldShowMinMaxColumns
      ? {
          exportTitle: 'Minimum',
          title: (
            <InfoTooltip title="Minimum data is presented as Current -> Recommended [% Delta]">Minimum</InfoTooltip>
          ),
          settingKey: 'minimum',
          dataIndex: asKeyOf<SearchPlanningDTO>('min_delta'),
          sorter: true,
          onCell: () => ({ style: { maxWidth: 'fit-content' } }),
          render: (_, record) => (
            <FlexSpace direction="row" gap={0}>
              {newMinMaxUi(record.current_min, record.recommended_min, record, isP21, record.min_delta)}
              <RecommendedMinPopover
                record={record}
                showOpOqVariant={config.isOpOqVariant}
                openSparseItemGrid={config.openSparseItemGrid}
              />
            </FlexSpace>
          ),
          exportValue: {
            'Current Minimum': asKeyOf<SearchPlanningDTO>('current_min'),
            'Recommended Minimum': asKeyOf<SearchPlanningDTO>('recommended_min'),
            'Minimum ∆%': asKeyOf<SearchPlanningDTO>('min_delta'),
          },
        }
      : {
          title: <b>{config.isOpOqVariant ? 'Order Point' : 'Minimum'}</b>,
          settingKey: 'minimum',
          children: [
            sortableNumberColumn({
              exportTitle: `Current ${config.isOpOqVariant ? 'Order Point' : 'Minimum'}`,
              title: 'Current',
              settingKey: 'currentMinimum',
              dataIndex: asKeyOf<SearchPlanningDTO>('current_min'),
              sorter: true,
              render: isP21 ? eoqUpToValueWithTooltip : (value) => formatNumber(value),
            }),
            getPlanningRecommendedMinColumn(config),
            sortableChangePercentColumn({
              exportTitle: `${config.isOpOqVariant ? 'Order Point' : 'Minimum'} ∆%`,
              title: '∆%',
              settingKey: 'minimumDelta',
              dataIndex: asKeyOf<SearchPlanningDTO>('min_delta'),
              sorter: true,
            }),
          ],
        },
    config.isOpOqVariant
      ? {
          title: <b>Order Quantity</b>,
          settingKey: 'orderQuantity',
          exportTitle: 'Order Quantity',
          dataIndex: asKeyOf<SearchPlanningDTO>('current_max'),
          sorter: true,
          sortDirections,
        }
      : !shouldShowMinMaxColumns
      ? {
          exportTitle: 'Maximum',
          title: (
            <InfoTooltip title="Maximum data is presented as Current -> Recommended [% Delta]">Maximum</InfoTooltip>
          ),
          settingKey: 'maximum',
          dataIndex: asKeyOf<SearchPlanningDTO>('max_delta'),
          sorter: true,
          onCell: () => ({ style: { maxWidth: 'fit-content' } }),
          render: (_, record) => (
            <FlexSpace direction="row" gap={0}>
              {newMinMaxUi(record.current_max, record.recommended_max, record, isP21, record.max_delta)}
              <RecommendedMaxPopover record={record} />
            </FlexSpace>
          ),
          exportValue: {
            'Current Maximum': asKeyOf<SearchPlanningDTO>('current_max'),
            'Recommended Maximum': asKeyOf<SearchPlanningDTO>('recommended_max'),
            'Maximum ∆%': asKeyOf<SearchPlanningDTO>('max_delta'),
          },
        }
      : {
          title: <b>Maximum</b>,
          settingKey: 'maximum',
          children: [
            sortableNumberColumn({
              exportTitle: 'Current Maximum',
              title: 'Current',
              settingKey: 'currentMaximum',
              dataIndex: asKeyOf<SearchPlanningDTO>('current_max'),
              sorter: true,
              render: isP21 ? eoqUpToValueWithTooltip : (value) => formatNumber(value),
            }),
            getPlanningRecommendedMaxColumn(),
            sortableChangePercentColumn({
              exportTitle: 'Maximum ∆%',
              title: '∆%',
              settingKey: 'maximumDelta',
              dataIndex: asKeyOf<SearchPlanningDTO>('max_delta'),
              sorter: true,
            }),
          ],
        },
    {
      title: (
        <InfoTooltip title="Recurrency always plans in the base unit of measure to ensure accuracy at time of purchase">
          UOM
        </InfoTooltip>
      ),
      settingKey: 'uom',
      dataIndex: asKeyOf<SearchPlanningDTO>('unit_of_measure'),
    },
    {
      title: <b>Demand</b>,
      settingKey: 'demand',
      children: [
        {
          title: (
            <InfoTooltip title="Demand patterns group items based on consistency of orders and variance in demand quantity. Seasonality is automatically factored into all non-Sparse patterns.">
              Pattern
            </InfoTooltip>
          ),
          exportTitle: 'Demand Pattern',
          dataIndex: asKeyOf<SearchPlanningDTO>('demand_pattern'),
          render: (demandPattern) => <DemandPatternTooltip demandPattern={demandPattern} />,
        },
        // using forecast_confidence as field for sorting, but showing predictability_tag in UI
        {
          title: (
            <InfoTooltip title="Estimated predictability of an item based on historical trends. Higher predictability leads to more accurate forecasts">
              Predictability
            </InfoTooltip>
          ),
          exportTitle: 'Demand Predictability',
          dataIndex: asKeyOf<SearchPlanningDTO>('predictability_tag_sort'),
          defaultSortOrder: 'descend' as const,
          sorter: true,
          sortDirections,
          render: (_, record) => record.predictability_tag,
          exportValue: asKeyOf<SearchPlanningDTO>('predictability_tag'),
        },
      ],
    },
    sortableDateColumn({
      title: 'Last Updated',
      settingKey: 'lastUpdated',
      dataIndex: asKeyOf<SearchPlanningDTO>('last_updated_at'),
      sorter: true,
      optional: false,
    }),
    {
      title: 'Status',
      settingKey: 'planningStatus',
      dataIndex: asKeyOf<SearchPlanningDTO>('planning_status'),
      render: (status: BadgeStatus, record) => <PlanningStatusBadge status={status} record={record} />,
    },
    {
      title: 'Actions',
      settingKey: 'actions',
      render: (_, record) => (
        <div
          className={css`
            display: flex;
            flex-direction: row;
          `}
        >
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="minMax"
                  icon={<CalculatorOutlined />}
                  onClick={() => {
                    showAsyncModal(UpdateReplenishmentSettingsModal, {
                      record,
                      searchIndexReload: config.searchIndexReload,
                    });
                  }}
                >
                  {config.isOpOqVariant ? 'OP/OQ' : 'Min/Max'}
                </Menu.Item>
                <Menu.Item
                  key="safetyStock"
                  icon={<ReconciliationOutlined />}
                  onClick={() => {
                    showAsyncModal(MultiSafetyStockModal, {
                      selection: { type: 'list', items: [record], count: 1 },
                      searchIndexReload: config.searchIndexReload,
                    });
                  }}
                >
                  Safety Stock
                </Menu.Item>
                <Menu.Item
                  key="leadTime"
                  icon={<CalendarOutlined />}
                  onClick={() => {
                    showAsyncModal(MultiLeadTimeModal, {
                      selection: { type: 'list', items: [record], count: 1 },
                      searchIndexReload: config.searchIndexReload,
                    });
                  }}
                >
                  Lead Time
                </Menu.Item>
                <Menu.Item
                  key="minStockFloor"
                  icon={<InsertRowBelowOutlined />}
                  onClick={() => {
                    showAsyncModal(MultiStockFloorModal, {
                      selection: { type: 'list', items: [record], count: 1 },
                      searchIndexReload: config.searchIndexReload,
                    });
                  }}
                >
                  Stock Floor
                </Menu.Item>
                <Menu.Divider />
                <ConditionalWrapper
                  condition={record.excluded && record.excluded_source === ExclusionSource.Automated}
                  wrapper={(children) => (
                    <Tooltip title={"This item-location's exclusion status is currently being managed by a Rule"}>
                      {children}
                    </Tooltip>
                  )}
                >
                  <div>
                    <Menu.Item
                      key="exclude"
                      disabled={record.excluded && record.excluded_source === ExclusionSource.Automated}
                      icon={record.excluded ? <CheckCircleOutlined /> : <StopOutlined />}
                      onClick={async () => {
                        const shouldExclude = !record.excluded;
                        const selection: MultiModalSelection = {
                          type: 'list',
                          items: [record],
                          count: 1,
                        };
                        const didChange = await changeRecordExcludedStatus(selection, shouldExclude);
                        if (didChange && config.searchIndexReload) config.searchIndexReload();
                      }}
                    >
                      {record.excluded ? 'Include' : 'Exclude'}
                    </Menu.Item>
                  </div>
                </ConditionalWrapper>

                <Menu.Item
                  key="flag"
                  icon={<FlagOutlined />}
                  onClick={async () => {
                    const shouldFlag = !record.flagged;
                    const selection: MultiModalSelection = {
                      type: 'list',
                      items: [record],
                      count: 1,
                    };
                    const didChange = await changeRecordFlagStatus(selection, shouldFlag);
                    if (didChange && config.searchIndexReload) config.searchIndexReload();
                  }}
                >
                  {record.planning_status === BadgeStatus.Flagged ? 'Resolve Flag' : 'Flag'}
                </Menu.Item>
              </Menu>
            }
          >
            <Button
              size="small"
              className={css`
                width: 50px;
              `}
            >
              <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      ),
    },
    // optional columns
    {
      title: 'Product Group',
      settingKey: 'itemGroup',
      dataIndex: asKeyOf<SearchPlanningDTO>('item_group'),
      optional: true,
    },
    {
      title: 'Safety Stock (Days)',
      settingKey: 'safetyStockDays',
      ...numberColumn({ dataIndex: asKeyOf<SearchPlanningDTO>('safety_stock_value') }),
      sorter: true,
      optional: true,
    },
    {
      title: 'Safety Stock Source',
      settingKey: 'safetyStockSource',
      dataIndex: asKeyOf<SearchPlanningDTO>('safety_stock_type'),
      render: (safetyStockType: P21SafetyStockType) => safetyStockSourceNames[safetyStockType],
      exportValue: (safetyStockType: P21SafetyStockType) => safetyStockSourceNames[safetyStockType],
      sorter: true,
      optional: true,
    },
    {
      title: 'Lead Time (Days)',
      settingKey: 'leadTimeDays',
      ...numberColumn({ dataIndex: asKeyOf<SearchPlanningDTO>('lead_time') }),
      sorter: true,
      optional: true,
    },
    {
      title: 'Lead Time Source',
      settingKey: 'leadTimesSource',
      dataIndex: asKeyOf<SearchPlanningDTO>('lead_time_source'),
      render: (leadTimeSource: P21LeadTimeSource) => leadTimeSourceNames[leadTimeSource],
      exportValue: (leadTimeSource: P21LeadTimeSource) => leadTimeSourceNames[leadTimeSource],
      sorter: true,
      optional: true,
    },
    {
      title: 'Order Cycle (Days)',
      settingKey: 'orderCycleDays',
      ...numberColumn({ dataIndex: asKeyOf<SearchPlanningDTO>('order_cycle_days') }),
      sorter: true,
      optional: true,
    },
    {
      title: 'Unit Cost',
      settingKey: 'unitCost',
      dataIndex: asKeyOf<SearchPlanningDTO>('unit_cost'),
      render: (amt) => formatUSD(amt, true),
      sorter: true,
      optional: true,
    },
    {
      title: 'Primary Supplier',
      settingKey: 'primarySupplier',
      dataIndex: asKeyOf<SearchPlanningDTO>('primary_supplier'),
      optional: true,
    },
    {
      title: 'Item Name',
      settingKey: 'itemName',
      dataIndex: asKeyOf<SearchPlanningDTO>('item_name'),
      sorter: true,
      sortDirections,
      optional: true,
    },
    {
      title: 'Excluded Until',
      settingKey: 'excludedUntil',
      dataIndex: asKeyOf<SearchPlanningDTO>('excluded_until'),
      render: (excludedUntil: string) => formatDate(excludedUntil),
      sorter: true,
      optional: true,
    },
    {
      title: <InfoTooltip title={firstUsageDateTooltipText}>First Usage Date</InfoTooltip>,
      settingKey: 'firstUsageDate',
      dataIndex: asKeyOf<SearchPlanningDTO>('first_usage_date'),
      render: (firstUsageDate: string | undefined) => (firstUsageDate ? formatDate(firstUsageDate) : '-'),
      sorter: true,
      optional: true,
    },
    {
      title: <InfoTooltip title={createdDateTooltipText}>Created At</InfoTooltip>,
      settingKey: 'erpCreatedDate',
      dataIndex: asKeyOf<SearchPlanningDTO>('erp_created_date'),
      render: (erpCreatedDate: string | undefined) => (erpCreatedDate ? formatDate(erpCreatedDate) : '-'),
      sorter: true,
      optional: true,
    },
    {
      title: 'Flag Reason',
      settingKey: 'flagReason',
      dataIndex: asKeyOf<SearchPlanningDTO>('flag_reason'),
      render: (flagReason: string | undefined) =>
        flagReason ? <EllipsisTruncatedParagraph maxLength={70} maxLines={3} label={flagReason} /> : '',
      sorter: true,
      optional: true,
    },
    sortableNumberColumn({
      title: 'Stock Floor',
      settingKey: 'minStockFloor',
      dataIndex: asKeyOf<SearchPlanningDTO>('min_stock_floor'),
      sorter: true,
      optional: true,
    }),
    !config.isOpOqVariant
      ? sortableNumberColumn({
          title: 'Min Order Quantity',
          settingKey: 'minOrderQuantity',
          dataIndex: asKeyOf<SearchPlanningDTO>('min_order_quantity'),
          sorter: true,
          optional: true,
        })
      : null,
    sortableNumberColumn({
      title: 'Net Stock',
      settingKey: 'netStock',
      dataIndex: asKeyOf<SearchPlanningDTO>('net_stock'),
      optional: true,
      render: (netStock, record) => (
        <>
          {formatNumber(netStock)} {record.unit_of_measure}
          <NetStockPopover
            showNonAggregatedWarning
            record={
              {
                netStock: record.net_stock,
                qtyOnHand: record.qty_on_hand,
                qtyAllocated: record.qty_allocated,
                qtyOnPurchaseOrder: record.qty_on_purchase_order,
                qtyInTransit: record.qty_in_transit,
                qtyInProcess: record.qty_in_process,
                qtyBackordered: record.qty_backordered,
                qtyReservedDueIn: record.qty_reserved_due_in,
                qtyInProduction: record.qty_in_production,
                qtyForProduction: record.qty_for_production,
                qtyQuarantined: record.qty_quarantined,
                qtyFrozen: record.qty_frozen,
                qtyNonPickable: record.qty_non_pickable,
                qtyOnReleaseSchedule: record.qty_on_release_schedule,
                qtyOnPendingBackorder: record.qty_on_pending_backorder,
                qtyOnBlanketPurchaseOrder: record.qty_on_blanket_purchase_order,
                qtyOnUnapprovedPurchaseOrder: record.qty_on_unapproved_purchase_order,
                unitOfMeasure: record.unit_of_measure,
              } as NetStockRecord
            }
          />
        </>
      ),
    }),
    sortableNumberColumn({
      title: (
        <InfoTooltip title="Estimates how many days your current Netstock will last, using the average daily demand from the next year of forecasted demand.">
          Days of Supply
        </InfoTooltip>
      ),
      settingKey: 'daysOfSupply',
      dataIndex: asKeyOf<SearchPlanningDTO>('days_of_supply'),
      optional: true,
    }),
  ];
};

export const getPlanningValueFacets = (config: {
  activeTenant: TenantDTO;
  activeUser: ExtendedUser;
}): (ValueFacet<SearchPlanningDTO> | null)[] => {
  const isPlanningHubAndSpoke = isHubAndSpokeEnabled();
  const isP21 = isTenantErpTypeP21(config.activeTenant.erpType);
  const isNetSuite = isTenantErpTypeNetSuite(config.activeTenant.erpType);

  return [
    {
      title: 'Status',
      field: asKeyOf<SearchPlanningDTO>('planning_status'),
      icon: <DashboardOutlined />,
      sortBy: 'none',
      options: [
        {
          label: labelByStatus[BadgeStatus.Updated],
          value: BadgeStatus.Updated as string,
        },
        {
          label: labelByStatus[BadgeStatus.Review],
          value: BadgeStatus.Review as string,
        },
        {
          label: labelByStatus[BadgeStatus.Excluded],
          value: BadgeStatus.Excluded as string,
        },
        {
          label: labelByStatus[BadgeStatus.Flagged],
          value: BadgeStatus.Flagged as string,
        },
      ],
    },
    {
      title: `${isPlanningHubAndSpoke ? 'Requirement ' : ''}Location`,
      field: asKeyOf<SearchPlanningDTO>('location'),
      queryPlaceholder: `Search ${isPlanningHubAndSpoke ? 'requirement ' : ''}locations`,
      icon: <EnvironmentOutlined />,
    },
    isPlanningHubAndSpoke
      ? {
          title: 'Replenishment Location',
          field: asKeyOf<SearchPlanningDTO>('replenishment_location'),
          queryPlaceholder: 'Search replenishment locations',
          icon: <EnvironmentOutlined />,
        }
      : null,
    isP21
      ? {
          title: 'ABC Class',
          field: asKeyOf<SearchPlanningDTO>('purchase_class_and_description'),
          sortBy: 'alpha',
          icon: <TagOutlined />,
        }
      : null,
    {
      title: 'Primary Supplier',
      field: asKeyOf<SearchPlanningDTO>('primary_supplier'),
      queryPlaceholder: 'Search primary suppliers',
      icon: <ShopOutlined />,
    },
    isP21
      ? {
          title: 'Vendor',
          field: asKeyOf<SearchPlanningDTO>('primary_vendor'),
          queryPlaceholder: 'Search primary vendors',
          icon: <ShopOutlined />,
        }
      : null,
    !isNetSuite
      ? {
          title: 'Product Group',
          field: asKeyOf<SearchPlanningDTO>('item_group'),
          queryPlaceholder: 'Search product groups',
          icon: <AppstoreOutlined />,
        }
      : null,
    isP21 &&
    shouldShowFeatureFlag(config.activeTenant, config.activeUser, TenantFeatureFlag.FeatureProductTypeSearchFilter)
      ? {
          title: 'Product Type',
          field: asKeyOf<SearchPlanningDTO>('product_type'),
          icon: <BuildOutlined />,
          options: [...productTypeFacetOptions],
          sortBy: 'none',
        }
      : null,
    getTenantSetting(TenantSettingKey.UiShowShortCode)
      ? {
          title: 'Short Code',
          field: asKeyOf<SearchPlanningDTO>('short_code'),
          queryPlaceholder: 'Search short codes',
          icon: <CodeOutlined />,
        }
      : null,
    getTenantSetting(TenantSettingKey.UiShowBuyerFilter)
      ? {
          title: 'Buyer',
          field: asKeyOf<SearchPlanningDTO>('buyer'),
          queryPlaceholder: 'Search buyers',
          icon: <UserOutlined />,
        }
      : null,
    {
      title: 'Demand Pattern',
      field: asKeyOf<SearchPlanningDTO>('demand_pattern'),
      icon: <StockOutlined />,
      options: demandPatternOptions,
      sortBy: 'none',
    },
    {
      title: 'Recent Pattern Changes',
      field: asKeyOf<SearchPlanningDTO>('demand_pattern_change'),
      icon: <FundOutlined />,
      options: demandPatternChangeOptions,
      sortBy: 'none',
    },
    {
      title: 'Predictability',
      field: asKeyOf<SearchPlanningDTO>('predictability_tag'),
      icon: <MonitorOutlined />,
      options: demandPredictabilityOptions,
      sortBy: 'none',
    },
    {
      title: 'Rules',
      field: asKeyOf<SearchPlanningDTO>('failed_rules'),
      icon: <SafetyCertificateOutlined />,
      queryPlaceholder: 'Search rules',
      sortBy: 'none',
    },
    getReplenishmentMethodFacetSettings(isP21),
    {
      title: 'Excluded Until',
      field: asKeyOf<SearchPlanningDTO>('excluded_until_category'),
      icon: <StopOutlined />,
      queryPlaceholder: 'Search Excluded Until',
      sortBy: 'none',
    },
  ];
};

const eoqUpToValueWithTooltip = (value: number, record: SearchPlanningDTO) => (
  <FlexSpace
    alignItems="start"
    gap={0}
    className={css`
      height: 32px;
      justify-content: flex-end;
    `}
  >
    {formatNumber(value)}
    {eoqTooltip(record)}
  </FlexSpace>
);

const eoqTooltip = (record: SearchPlanningDTO) => (
  <>
    {(record.replenishment_method === ReplenishmentMethod.EOQ ||
      record.replenishment_method === ReplenishmentMethod.UpTo) && (
      <Tooltip
        title={`Min and Max are not used in system as this item is using the ${record.replenishment_method} replenishment method`}
        placement="right"
      >
        <div
          className={css`
            position: relative;
          `}
        >
          <span
            className={css`
              color: ${theme.colors.malibu[600]};
              top: 1px;
              right: 1px;
              margin-left: 2px;
            `}
          >
            *
          </span>
        </div>
      </Tooltip>
    )}
  </>
);

const newMinMaxUi = (
  current: number,
  recommended: number,
  record: SearchPlanningDTO,
  isP21: boolean,
  delta?: number,
) => (
  <>
    <FlexSpace
      direction="row"
      gap={0}
      className={css`
        text-wrap: nowrap;
      `}
    >
      {formatNumber(current)}
      {isP21 ? eoqTooltip(record) : null}
      &nbsp;
      {'->'}
      &nbsp;
      <strong>{`${formatNumber(recommended)}`}</strong>
    </FlexSpace>
    &nbsp;
    <div>[</div>
    {!delta ? (
      <CaretUpFilled style={{ color: getChangeColor(1) }} />
    ) : delta > 0 ? (
      <CaretUpFilled style={{ color: getChangeColor(delta) }} />
    ) : (
      <CaretDownFilled style={{ color: getChangeColor(delta) }} />
    )}
    {formatPercent(delta, 0)}
    <div>]</div>
  </>
);

const getProjectedInventoryChangeColumn = (optional: boolean): PersistedColumn<SearchPlanningDTO> => ({
  title: (
    <InfoTooltip title="Projected inventory change provides a directional indication showing how much the recommendation would impact the current state of inventory levels.">
      Projected Inventory Change
    </InfoTooltip>
  ),
  settingKey: 'projectedInventoryChange',
  dataIndex: asKeyOf<SearchPlanningDTO>('projected_inventory_change_abs'),
  sorter: true,
  render: (_: number, record: SearchPlanningDTO) =>
    record.projected_inventory_change !== undefined ? (
      <FlexSpace direction="row" gap={0}>
        <div
          className={css`
            display: inline-flex;
            color: ${record.projected_inventory_change < 0 ? 'black' : theme.colors.info[700]};
          `}
        >
          {formatUSD(record.projected_inventory_change, true)}
        </div>
        <ProjectedInventoryChangePopover record={record} />
      </FlexSpace>
    ) : (
      <></>
    ),
  exportValue: asKeyOf<SearchPlanningDTO>('projected_inventory_change'),
  optional,
});

const getPlanningRecommendedMinColumn = (config: {
  isOpOqVariant: boolean;
  openSparseItemGrid?: () => void;
}): PersistedColumn<SearchPlanningDTO> => ({
  exportTitle: `Recommended ${config.isOpOqVariant ? 'Order Point' : 'Minimum'}`,
  title: 'Recommended',
  settingKey: 'recommendedMinimum',
  dataIndex: asKeyOf<SearchPlanningDTO>('recommended_min'),
  align: 'right',
  sorter: true,
  onCell: () => ({ style: { minWidth: '135px' } }),
  render: (recommendedMin, record) => (
    <div
      className={css`
        display: inline-flex;
      `}
    >
      {formatNumber(recommendedMin)}
      <RecommendedMinPopover
        record={record}
        showOpOqVariant={config.isOpOqVariant}
        openSparseItemGrid={config.openSparseItemGrid}
      />
    </div>
  ),
});

const getPlanningRecommendedMaxColumn = (): PersistedColumn<SearchPlanningDTO> => ({
  exportTitle: 'Recommended Maximum',
  settingKey: 'recommendedMaximum',
  title: `Recommended`,
  dataIndex: asKeyOf<SearchPlanningDTO>('recommended_max'),
  align: 'right',
  sorter: true,
  render: (recommendedMax, record) => (
    <div
      className={css`
        display: inline-flex;
      `}
    >
      {formatNumber(recommendedMax)} <RecommendedMaxPopover record={record} />
    </div>
  ),
});

const getReplenishmentMethodFacetSettings = (shouldShowOpOq?: boolean) => {
  const minMaxOptionValue = [RecurrencyReplenishmentMethod.MinMax, RecurrencyReplenishmentMethod.Unsupported];

  if (shouldShowOpOq) {
    return {
      title: 'Replenishment Method',
      renderType: FacetRenderType.Pill,
      field: asKeyOf<SearchPlanningDTO>('recurrency_replenishment_method'),
      defaultValue: minMaxOptionValue,
      options: [
        {
          label: RecurrencyReplenishmentMethod.MinMax,
          value: minMaxOptionValue,
        },
        {
          label: RecurrencyReplenishmentMethod.OPOQ,
          value: RecurrencyReplenishmentMethod.OPOQ,
        },
      ],
    };
  }

  return {
    field: asKeyOf<SearchPlanningDTO>('recurrency_replenishment_method'),
    defaultValue: minMaxOptionValue,
    hidden: true,
  };
};

const getCreatedDateTooltipText = (inheritanceEnabled: boolean, hubAndSpokeEnabled: boolean) => {
  const baseDescription = 'The date this item-location combination was created in your ERP';
  const hubAndSpokeText = hubAndSpokeEnabled ? '. This excludes records from downstream replenishment locations' : '';
  const ancestorText = hubAndSpokeEnabled ? 'and any ancestor items' : '. This excludes any ancestor items';
  return `${baseDescription}${hubAndSpokeText}${inheritanceEnabled ? ` ${ancestorText}` : ''}.`;
};

const getFirstSoldDateTooltipText = (inheritanceEnabled: boolean, hubAndSpokeEnabled: boolean) => {
  const baseDescription = 'The date of the first usage in the ERP.';
  const hubAndSpokeText = hubAndSpokeEnabled
    ? 'This includes usage lines from downstream replenishment locations.'
    : '';
  const ancestorText = inheritanceEnabled ? 'This excludes any ancestor items.' : '';
  return `${baseDescription} ${hubAndSpokeText} ${ancestorText}`;
};

export const getSummaryDataFromResolutionData = (resolutionData: ItemWithResolutionData[]) => {
  const acceptAndOverridenItems = resolutionData.filter(
    (item) =>
      item.resolution === DailyWorkflowTaskResolution.Accepted ||
      item.resolution === DailyWorkflowTaskResolution.Overriden,
  );

  const flaggedItems = resolutionData.filter((item) => item.resolution === DailyWorkflowTaskResolution.Flagged);

  return {
    resolutionCounts: {
      [DailyWorkflowTaskResolution.Flagged]: flaggedItems.length,
      [DailyWorkflowTaskResolution.Excluded]: resolutionData.filter(
        (item) => item.resolution === DailyWorkflowTaskResolution.Excluded,
      ).length,
      [DailyWorkflowTaskResolution.Accepted]: resolutionData.filter(
        (item) => item.resolution === DailyWorkflowTaskResolution.Accepted,
      ).length,
      [DailyWorkflowTaskResolution.Overriden]: resolutionData.filter(
        (item) => item.resolution === DailyWorkflowTaskResolution.Overriden,
      ).length,
      [DailyWorkflowTaskResolution.Ignored]: resolutionData.filter(
        (item) => item.resolution === DailyWorkflowTaskResolution.Ignored,
      ).length,
    },
    acceptanceHistory: {
      items: [],
      totalCount: 0,
      minimumStreakSize: 0,
    },
    modifiedMinMaxList: {
      items: acceptAndOverridenItems.map((item) => ({
        ...item,
        ...item.resolutionData,
        newMin: item.resolutionData.min,
        newMax: item.resolutionData.max,
      })),
      totalCount: acceptAndOverridenItems.length,
    },
    projectedInventoryChange: 0,
    flaggedItemList: { items: flaggedItems, totalCount: flaggedItems.length },
  };
};
